import type { AxiosRequestConfig } from 'axios';

import { API_ENDPOINTS } from '@/utils/constants/end-points';
import { buildQueryParams } from '@/utils/service-utils';

import apiClient from '../api-client';
import { AwardListSaveAsDraftPayload } from '../types';

export const createAward = async (payload: any) => {
  const res = await apiClient.post(`${API_ENDPOINTS?.AWARD}`, payload);
  return res?.data;
};

export const updateAward = async (payload: any, awardId: string | number) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AWARD}/${awardId}`,
    payload
  );
  return res?.data;
};

export const fetchAwardByNo = async (awardNo: string) => {
  const res = await apiClient.get(`${API_ENDPOINTS?.AWARD}/${awardNo}`);
  return res?.data?.data;
};

export const deleteAward = async (awardNo: string) => {
  const res = await apiClient.delete(`${API_ENDPOINTS?.AWARD}/${awardNo}`);
  return res?.data;
};

interface AxiosRequestConfigWithSkipErrorToast<
  D = unknown,
> extends AxiosRequestConfig<D> {
  skipErrorToast?: boolean;
}

export const fetchAwardApprovalList = async (params: {
  offset?: number;
  limit?: number;
  search?: string;
  workflowStatusId?: number | string | (number | string)[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
}) => {
  const searchParams = buildQueryParams(params, false);
  const queryString = searchParams.toString();
  const res = await apiClient.get(
    `${API_ENDPOINTS?.MAINTENANCE}/awards?${queryString}`
  );
  return res?.data?.data;
};

export const awardDetailsForApproval = async (txnId: string) => {
  const res = await apiClient.get(`${API_ENDPOINTS?.MAINTENANCE}/${txnId}`);
  return res?.data?.data;
};

export const checkerActionAward = async (payload: {
  txnId: string;
  action: string;
  remarks?: string;
}) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS?.AWARD}/checker-action`,
    payload
  );
  return res?.data;
};

export const fetchManageAwardList = async (params: {
  offset?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  StatusId?: number;
  PlanTypeId?: number;
  isExport?: boolean;
}) => {
  const searchParams = buildQueryParams(params);
  const queryString = searchParams.toString();
  const res = await apiClient.get(`${API_ENDPOINTS?.AWARD}?${queryString}`);
  return res?.data?.data;
};
export const reviseAward = async (payload: any, txnId: string) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AWARD}/revise/${txnId}`,
    payload
  );
  return res?.data;
};

export const fetchAwardLetterList = async (params: {
  offset?: number;
  limit?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
}) => {
  const { offset, limit, searchTerm, sortBy, sortDir, isExport } = params;
  const searchParams = new URLSearchParams();
  if (isExport) {
    searchParams.set('IsExport', 'true');
  } else {
    if (offset !== undefined) searchParams.set('Offset', String(offset));
    if (limit !== undefined) searchParams.set('Limit', String(limit));
  }
  if (searchTerm) searchParams.set('SearchTerm', searchTerm);
  if (sortBy) searchParams.set('SortBy', sortBy);
  if (sortDir) searchParams.set('SortDir', sortDir);
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/list?${searchParams.toString()}`
  );
  return res?.data?.data;
};

export const fetchAwardLetterSummary = async (awardListId: number | string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/summary?AwardListId=${awardListId}`
  );
  return res?.data?.data;
};

export const suppressAwardLetterEmails = async (
  awardListId: number | string,
  payload: {
    updates?: { offerId: number; isSuppress: boolean }[];
    isSuppress: boolean;
    type: string;
    suppressAll?: boolean;
  }
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardLetter}/${awardListId}/suppress`,
    payload
  );
  return res?.data;
};

export const generateAwardLetter = async (awardListId: number | string) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardLetter}/${awardListId}/generate`
  );
  return res?.data;
};

export const fetchAwardLetterPreview = async (params: {
  awardListId: number | string;
  offset?: number;
  limit?: number;
  searchTerm?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
}) => {
  const { awardListId, offset, limit, searchTerm, sortBy, sortDir, isExport } =
    params;
  const searchParams = new URLSearchParams();
  searchParams.set('AwardListId', String(awardListId));
  if (isExport) {
    searchParams.set('IsExport', 'true');
  } else {
    if (offset !== undefined) searchParams.set('Offset', String(offset));
    if (limit !== undefined) searchParams.set('Limit', String(limit));
  }
  if (searchTerm) searchParams.set('SearchTerm', searchTerm);
  if (sortBy) searchParams.set('SortBy', sortBy);
  if (sortDir) searchParams.set('SortDir', sortDir);
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/preview?${searchParams.toString()}`
  );
  return res?.data?.data;
};

// ─── Final Award letter services ──────────────────────────────────────────────
// Kept separate from the contingent award-letter services above (which key off
// AwardListId). The Final Award flow keys off AwardNo and may diverge further,
// so these are intentionally independent even where currently similar.

export const fetchFinalAwardLetterPreview = async (params: {
  awardNo: number | string;
  offset?: number;
  limit?: number;
  searchTerm?: string;
  searchField?: string;
  status?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  isExport?: boolean;
}) => {
  const {
    awardNo,
    offset,
    limit,
    searchTerm,
    searchField,
    status,
    sortBy,
    sortDir,
    isExport,
  } = params;
  const searchParams = new URLSearchParams();
  searchParams.set('AwardNo', String(awardNo));
  if (isExport) {
    searchParams.set('IsExport', 'true');
  } else {
    if (offset !== undefined) searchParams.set('Offset', String(offset));
    if (limit !== undefined) searchParams.set('Limit', String(limit));
  }
  if (searchTerm) searchParams.set('SearchTerm', searchTerm);
  if (searchField) searchParams.set('SearchField', searchField);
  if (status) searchParams.set('Status', status);
  if (sortBy) searchParams.set('SortBy', sortBy);
  if (sortDir) searchParams.set('SortDir', sortDir);
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/final-preview?${searchParams.toString()}`
  );
  return res?.data?.data;
};

export const fetchFinalAwardLetterSummary = async (
  awardNo: number | string
) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/${awardNo}/summary-final`
  );
  return res?.data?.data;
};

export const suppressFinalAwardLetterEmails = async (
  awardNo: number | string,
  payload: {
    updates?: { userId: number; isSuppress: boolean }[];
    isSuppress: boolean;
    suppressAll?: boolean;
  }
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardLetter}/${awardNo}/suppress-final`,
    payload
  );
  return res?.data;
};

export const generateFinalAwardLetter = async (awardNo: number | string) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardLetter}/${awardNo}/generate-final`
  );
  return res?.data;
};

// GET /awardletter/{awardNo}/template-preview-final — the Final flow's
// template preview is keyed by the award no (the contingent route keeps
// template-preview?awardListId=...).
export const fetchFinalTemplatePreviewByAwardNo = async (
  awardNo: number | string
) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/${awardNo}/template-preview-final`
  );
  return res?.data?.data;
};

export const fetchAwardListDetails = async (params: {
  offset?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  awardNo?: string;
  isExport?: boolean;
  workFlowStatusId?: number;
  workFlowActionId?: number;
}) => {
  const searchParams = buildQueryParams(params);
  const queryString = searchParams.toString();
  const res = await apiClient.get(`${API_ENDPOINTS?.AwardList}?${queryString}`);
  return res?.data?.data;
};

export const awardListFileUpload = async (
  payload: {
    formData: FormData;
    planTypeId: string;
  },
  onUploadProgress?: (progress: number) => void
) => {
  const { formData, planTypeId } = payload;
  formData.append('planTypeId', planTypeId);
  formData.append('fileId', '0'); // Append fileId as 0 for new uploads
  const requestConfig: AxiosRequestConfigWithSkipErrorToast<FormData> = {
    skipErrorToast: true,
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: progressEvent => {
      if (onUploadProgress && progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onUploadProgress(percent);
      }
    },
  };
  const res = await apiClient.post(
    `${API_ENDPOINTS?.AwardList}/upload`,
    formData,
    requestConfig
  );
  return res?.data;
};

export const fetchUploadedFileDetails = async (
  fileId: string,
  params?: {
    offset?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    status?: string;
    isExport?: boolean;
    isExcluded?: boolean;
  }
) => {
  const searchParams = params
    ? buildQueryParams(params)
    : new URLSearchParams();

  const queryString = searchParams.toString();
  const url = queryString
    ? `${API_ENDPOINTS?.AwardList}/${fileId}/details?${queryString}`
    : `${API_ENDPOINTS?.AwardList}/${fileId}/details`;
  const res = await apiClient.get(url);
  return res?.data;
};

export const awardListFileReUpload = async (
  payload: {
    formData: FormData;
    fileId: string;
  },
  onUploadProgress?: (progress: number) => void
) => {
  const { formData, fileId } = payload;
  const requestConfig: AxiosRequestConfigWithSkipErrorToast<FormData> = {
    skipErrorToast: true,
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: progressEvent => {
      if (onUploadProgress && progressEvent.total) {
        const percent = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onUploadProgress(percent);
      }
    },
  };
  const res = await apiClient.post(
    `${API_ENDPOINTS?.AwardList}/${fileId}/reupload`,
    formData,
    requestConfig
  );
  return res?.data;
};

export const awardListSaveAsDraft = async (
  fileId: string,
  payload: AwardListSaveAsDraftPayload
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardList}/${fileId}/exclude`,
    payload
  );
  return res?.data;
};

export const awardTemplateTypeList = async () => {
  const res = await apiClient.get(`${API_ENDPOINTS?.MASTER}/templates`);
  return res?.data?.data;
};

export const templateCategoryList = async () => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.MASTER}/template-categories`
  );
  return res?.data?.data;
};

export const shareCreditTemplateList = async (
  categoryId: number,
  isBuiltIn?: boolean
) => {
  const searchParams = new URLSearchParams();
  searchParams.set('categoryId', String(categoryId));
  // Only appended when explicitly requested — the SGX letter dropdown asks for
  // non-built-in (custom) templates; Step 4 fetches the full list unfiltered.
  if (isBuiltIn !== undefined) {
    searchParams.set('isBuiltIn', String(isBuiltIn));
  }
  const res = await apiClient.get(
    `${API_ENDPOINTS?.MASTER}/templates?${searchParams.toString()}`
  );
  return res?.data?.data;
};

export const fetchAwardSummaryListByFileId = async (params: {
  fileId: string;
  offset?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  detailsOffset?: number;
  detailsLimit?: number;
  sortDir?: 'asc' | 'desc';
  IsExcluded?: boolean;
  isExport?: boolean;
  status?: string;
}) => {
  const { fileId, isExport } = params;
  const searchParams = new URLSearchParams();
  if (isExport) {
    searchParams.set('IsExport', 'true');
  }
  if (params?.detailsLimit) {
    searchParams.set('DetailLimit', String(params?.detailsLimit));
  }

  if (params?.detailsOffset) {
    searchParams.set('DetailOffset', String(params?.detailsOffset));
  }
  if (params?.offset) {
    searchParams.set('SummaryOffset', String(params?.offset));
  }
  if (params?.limit) {
    searchParams.set('SummaryLimit', String(params?.limit));
  }
  if (params?.search) {
    searchParams.set('SearchField', params?.search);
  }
  if (params?.sortBy) {
    searchParams.set('SortBy', params?.sortBy);
  }
  if (params?.IsExcluded !== undefined) {
    searchParams.set('IsExcluded', String(params?.IsExcluded));
  }
  if (params?.sortDir) {
    searchParams.set('SortDir', params?.sortDir);
  }
  const queryString = searchParams.toString();
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardList}/${fileId}/summary?${queryString}`
  );
  return res?.data?.data;
};

export const fetchAwardSummaryListByFileIdForView = async (params: {
  fileId: string;
  offset?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  detailsOffset?: number;
  detailsLimit?: number;
  sortDir?: 'asc' | 'desc';
  IsExcluded?: boolean;
  isExport?: boolean;
  status?: string;
}) => {
  const { fileId, isExport } = params;
  const searchParams = new URLSearchParams();
  if (isExport) {
    searchParams.set('IsExport', 'true');
  }
  if (params?.detailsLimit) {
    searchParams.set('DetailLimit', String(params?.detailsLimit));
  }

  if (params?.detailsOffset) {
    searchParams.set('DetailOffset', String(params?.detailsOffset));
  }
  if (params?.offset) {
    searchParams.set('SummaryOffset', String(params?.offset));
  }
  if (params?.limit) {
    searchParams.set('SummaryLimit', String(params?.limit));
  }
  if (params?.search) {
    searchParams.set('SearchField', params?.search);
  }
  if (params?.sortBy) {
    searchParams.set('SortBy', params?.sortBy);
  }
  if (params?.IsExcluded !== undefined) {
    searchParams.set('IsExcluded', String(params?.IsExcluded));
  }
  if (params?.sortDir) {
    searchParams.set('SortDir', params?.sortDir);
  }
  const queryString = searchParams.toString();
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardList}/${fileId}/summary/review?${queryString}`
  );
  return res?.data?.data;
};

export const awardListSubmit = async (fileId: string, payload: any) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardList}/${fileId}/submit`,
    payload
  );
  return res?.data;
};

export const awardListExcludedItems = async (fileId: string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardList}/${fileId}/excludeditems`
  );
  return res?.data?.data;
};

export const awardListForApproval = async (
  fileId: string,
  remarks?: string
) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardList}/${fileId}/send-for-approval`,
    {
      remarks: remarks || '',
    }
  );
  return res?.data;
};

export const approveAwardList = async (fileId: string, payload: any) => {
  const res = await apiClient.put(
    `${API_ENDPOINTS?.AwardList}/${fileId}/approve-award-list`,
    payload
  );
  return res?.data;
};

export const deleteAwardList = async (fileId: string) => {
  const res = await apiClient.delete(`${API_ENDPOINTS?.AwardList}/${fileId}`);
  return res?.data;
};

export const downloadAwardListByFileID = async (fileId: string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardList}/${fileId}/approved-details-export`
  );
  return res?.data?.data;
};

/**
 * View Awards status tab. "Accepted" includes auto-accepted (view-acceptance)
 * awards; "Rejected" is declined awards.
 */
export type ViewAwardActionStatusFilter =
  | 'ActionRequired'
  | 'Accepted'
  | 'Rejected';

/**
 * Per-status totals for the View Awards tabs and the "N awards require you to
 * take action" banner, across every page and with every filter applied except
 * the status tab. The three tabs always add up to `all`.
 */
export interface ViewAwardStatusCounts {
  all: number;
  actionRequired: number;
  accepted: number;
  rejected: number;
}

export const viewAwardListDetails = async (params: {
  awardNo?: string;
  offset?: number;
  limit?: number;
  searchTerm?: string;
  searchEmployee?: string;
  /** One employee picked in the search (Users.id); exact, wins over searchEmployee. */
  employeeUserId?: number;
  planTypeId?: number;
  GrantYear?: string;
  VestingYear?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  /** Every matching row in one response; offset/limit are ignored. */
  isExport?: boolean;
  /** Status tab; omitted for All. See ViewAwardActionStatusFilter. */
  actionStatus?: ViewAwardActionStatusFilter;
}) => {
  const searchParams = new URLSearchParams();

  if (params?.awardNo) searchParams.set('AwardNo', params.awardNo);
  if (params?.actionStatus)
    searchParams.set('ActionStatus', params.actionStatus);
  if (params?.offset !== undefined)
    searchParams.set('Offset', String(params.offset));
  if (params?.limit !== undefined)
    searchParams.set('Limit', String(params.limit));
  if (params?.searchTerm) searchParams.set('SearchTerm', params.searchTerm);
  if (params?.searchEmployee)
    searchParams.set('SearchEmployee', params.searchEmployee);
  if (params?.employeeUserId)
    searchParams.set('EmployeeUserId', String(params.employeeUserId));
  if (params?.planTypeId)
    searchParams.set('PlanType', String(params.planTypeId));
  if (params?.GrantYear) searchParams.set('GrantYear', params.GrantYear);
  if (params?.VestingYear) searchParams.set('VestingYear', params.VestingYear);
  if (params?.sortBy) searchParams.set('SortBy', params.sortBy);
  if (params?.sortDir) searchParams.set('SortDir', params.sortDir);
  if (params?.isExport) searchParams.set('IsExport', 'true');

  const queryString = searchParams.toString();
  const url = queryString
    ? `${API_ENDPOINTS?.VIEW}?${queryString}`
    : `${API_ENDPOINTS?.VIEW}`;

  const res = await apiClient.get(url);
  return res?.data?.data;
};

export const viewAwardListByAwardNo = async (params: {
  awardNo: string;
  searchEmployee?: string;
}) => {
  const searchParams = new URLSearchParams();

  if (params?.searchEmployee)
    searchParams.set('SearchEmployee', params.searchEmployee);

  const queryString = searchParams.toString();
  const url = queryString
    ? `${API_ENDPOINTS?.VIEW}/${params.awardNo}?${queryString}`
    : `${API_ENDPOINTS?.VIEW}/${params.awardNo}`;

  const res = await apiClient.get(url);
  return res?.data?.data;
};

/** Which View Award letter to fetch; the API defaults to Contingent. */
export type ViewAwardLetterType = 'Contingent' | 'Final';

export const fetchAwardListDetailsForPreview = async (params: {
  awardNo: string;
  searchEmployee?: string;
  letterType?: ViewAwardLetterType;
}) => {
  const searchParams = new URLSearchParams();

  if (params?.searchEmployee)
    searchParams.set('SearchEmployee', params.searchEmployee);
  if (params?.letterType) searchParams.set('letterType', params.letterType);

  const queryString = searchParams.toString();
  const url = queryString
    ? `${API_ENDPOINTS?.VIEW}/${params.awardNo}/letter?${queryString}`
    : `${API_ENDPOINTS?.VIEW}/${params.awardNo}/letter`;

  // A final letter that doesn't exist yet is a 404 the caller shows inline,
  // not an error toast.
  const finalLetterConfig: AxiosRequestConfigWithSkipErrorToast = {
    skipErrorToast: true,
  };
  const res =
    params?.letterType === 'Final'
      ? await apiClient.get(url, finalLetterConfig)
      : await apiClient.get(url);
  return res?.data?.data;
};

// Contingent template preview, keyed by the award list id.
export const fetchTemplatePreviewById = async (templateId: number | string) => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardLetter}/template-preview?awardListId=${templateId}`
  );
  return res?.data?.data;
};

export const fetchSampleTemplate = async () => {
  const res = await apiClient.get(
    `${API_ENDPOINTS?.AwardList}/sample-template`
  );
  return res?.data;
};

export const handleAcceptorReject = async (
  awardNo: string,
  payload: { offerId: number; acceptanceType: 'A' | 'D' }
) => {
  const res = await apiClient.post(
    `${API_ENDPOINTS?.VIEW}/${awardNo}/respond`,
    payload
  );
  return res?.data;
};

export const fetchFairValueList = async (payload: {
  awardYear?: number;
  planTypeCode?: string;
  searchField?: string;
  pagination: {
    offset?: number;
    limit?: number;
    isExport?: boolean;
    totalCount?: number;
    sortBy?: string;
    sortDir?: string;
  };
}) => {
  const res = await apiClient.post(
    `/AwardManagement/FairValue/Search`,
    payload
  );
  return res?.data?.data;
};

export const updateFairValue = async (payload: {
  awardNo: string;
  tranche: number;
  performanceMeasureID: number;
  isWeightedAvg: boolean;
  performanceConditionFairValue: number;
  performanceConditionExpectedFactor: number;
}) => {
  const res = await apiClient.put(`/AwardManagement/FairValue/Edit`, payload);
  return res?.data;
};
